/**
 * React Query Hooks for Models
 *
 * Query hooks for model options
 */

import { useQuery, UseQueryResult } from '@tanstack/react-query';
import { modelsApi } from '../api';
import type { ModelInfo, AllModelsResponse } from '@/types';

export const MODELS_QUERY_KEYS = {
  models: ['models'] as const,
  chatModels: () => [...MODELS_QUERY_KEYS.models, 'chat'] as const,
  memoryModels: () => [...MODELS_QUERY_KEYS.models, 'memory'] as const,
  embeddingModels: () => [...MODELS_QUERY_KEYS.models, 'embedding'] as const,
  allModels: () => [...MODELS_QUERY_KEYS.models, 'all'] as const,
};

/**
 * Get all available chat models
 */
export function useChatModelsQuery(): UseQueryResult<ModelInfo[], Error> {
  return useQuery({
    queryKey: MODELS_QUERY_KEYS.chatModels(),
    queryFn: () => modelsApi.listChatModels(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get all available memory models
 */
export function useMemoryModelsQuery(): UseQueryResult<ModelInfo[], Error> {
  return useQuery({
    queryKey: MODELS_QUERY_KEYS.memoryModels(),
    queryFn: () => modelsApi.listMemoryModels(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get all available embedding models
 */
export function useEmbeddingModelsQuery(): UseQueryResult<ModelInfo[], Error> {
  return useQuery({
    queryKey: MODELS_QUERY_KEYS.embeddingModels(),
    queryFn: () => modelsApi.listEmbeddingModels(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}

/**
 * Get all model options in a single call
 */
export function useAllModelsQuery(): UseQueryResult<AllModelsResponse, Error> {
  return useQuery({
    queryKey: MODELS_QUERY_KEYS.allModels(),
    queryFn: () => modelsApi.listAllModels(),
    staleTime: 1000 * 60 * 5, // 5 minutes
  });
}
